'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ExternalLink, InfoIcon, KeyRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Schema
// ============================================================================

const hyrosFormSchema = z.object({
  apiKey: z.string().min(1, 'API key is required').trim(),
  newCustomersOnly: z.boolean().optional().default(false),
  sendOrders: z.boolean().optional().default(true),
})

type FormValues = z.infer<typeof hyrosFormSchema>

interface HyrosFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function HyrosForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: HyrosFormProps) {
  const { t } = useTranslation('integrations')

  const hasExistingKey =
    defaultValues.credentials?.apiKey && defaultValues.credentials.apiKey.length > 0

  const form = useForm<FormValues>({
    resolver: zodResolver(hyrosFormSchema) as any,
    mode: 'all',
    defaultValues: {
      apiKey: hasExistingKey ? '\u2022'.repeat(32) : '',
      newCustomersOnly: defaultValues.settings?.newCustomersOnly ?? false,
      sendOrders: defaultValues.settings?.sendOrders ?? true,
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    watch,
    setValue,
  } = form

  const handleFormSubmit = async (data: FormValues) => {
    await onSubmit({
      credentials: {
        // Don't overwrite the saved key if the merchant didn't touch
        // the (masked) field — just like the Converge form does.
        apiKey: touchedFields.apiKey ? data.apiKey.trim() : defaultValues.credentials?.apiKey,
      },
      settings: {
        newCustomersOnly: data.newCustomersOnly ?? false,
        sendOrders: data.sendOrders ?? true,
      },
    })
  }

  return (
    <form
      id="integration-config-form"
      onSubmit={handleSubmit(handleFormSubmit)}
      className="space-y-6"
    >
      {/* About */}
      <Alert>
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>
          {t('hyros.aboutTitle')}
        </AlertTitle>
        <AlertDescription>
          {t('hyros.aboutDescription')}
        </AlertDescription>
      </Alert>

      {/* API key */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            {t('hyros.apiKeyTitle')}
          </CardTitle>
          <CardDescription>
            {t('hyros.apiKeyDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor="apiKey">
            {t('hyros.apiKeyLabel')}
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <Input
            id="apiKey"
            type="password"
            placeholder={t('hyros.apiKeyPlaceholder')}
            disabled={isLoading}
            {...register('apiKey')}
          />
          {errors.apiKey && touchedFields.apiKey && (
            <p className="text-xs text-destructive">{errors.apiKey.message as string}</p>
          )}
        </CardContent>
      </Card>

      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle>{t('hyros.settingsTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-start gap-3">
            <Checkbox
              id="sendOrders"
              checked={!!watch('sendOrders')}
              onCheckedChange={(checked) =>
                setValue('sendOrders', checked as boolean, { shouldTouch: true })
              }
              disabled={isLoading}
            />
            <div className="space-y-1">
              <Label htmlFor="sendOrders" className="cursor-pointer font-medium">
                {t('hyros.sendOrdersLabel')}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t('hyros.sendOrdersHelp')}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              id="newCustomersOnly"
              checked={!!watch('newCustomersOnly')}
              onCheckedChange={(checked) =>
                setValue('newCustomersOnly', checked as boolean, { shouldTouch: true })
              }
              disabled={isLoading}
            />
            <div className="space-y-1">
              <Label htmlFor="newCustomersOnly" className="cursor-pointer font-medium">
                {t('hyros.newCustomersOnlyLabel')}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t('hyros.newCustomersOnlyHelp')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* What we send */}
      <Card>
        <CardHeader>
          <CardTitle>{t('hyros.payloadTitle')}</CardTitle>
          <CardDescription>
            {t('hyros.payloadDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>{t('hyros.bullet1')}</li>
            <li>{t('hyros.bullet2')}</li>
            <li>{t('hyros.bullet3')}</li>
            <li>{t('hyros.bullet4')}</li>
            <li>{t('hyros.bullet5')}</li>
          </ul>
        </CardContent>
      </Card>

      {/* Docs link */}
      <div>
        <a
          href="https://hyros.docs.apiary.io/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary underline"
        >
          {t('hyros.officialDocs')}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </form>
  )
}
